import { Icon } from "@circle-react-shared/Icon";
import { DIRECT_CHAT_ROOM } from "@circle-react/components/constants";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { chatRoomLinkOptions } from "@circle-react/helpers/urlHelpers";
import { useChatRoom } from "@circle-react/hooks/chatsV2/useChatRoom";
import { Button as CompassButton } from "@circleco/compass/components/Button";
import { IconButton } from "@circleco/compass/components/IconButton";
import { useNavigate } from "@tanstack/react-router";
import { noop } from "lodash";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useCircleV4 } from "@/react/hooks/useCircleV4";
import { usePreviousLocationState } from "@/react/hooks/usePreviousLocationState";

import type { MessageButtonProps } from "./types";

const sizeClasses = {
  sm: "!h-7 !px-3 gap-1.5",
  md: "!h-8 !px-4 gap-2",
  lg: "!h-10 !px-5 gap-2.5",
  full: "w-full",
};

const iconSizes = {
  sm: 14,
  md: 16,
  lg: 18,
  full: 16,
};

export const MessageButton = ({
  memberId,
  onMessageSent,
  closeParent,
  iconOnly = false,
  variant = "default",
  size = "md",
  className,
  disabled,
  fullWidth = false,
}: MessageButtonProps) => {
  const { isCircleV4 } = useCircleV4();
  const navigate = useNavigate();
  const { isViewOnlyMasquerading } = usePunditUserContext();
  const { findOrCreateChatRoom } = useChatRoom();
  const previousLocationState = usePreviousLocationState();

  const { mutate: handleMessage, isPending: isLoading } = useMutation({
    mutationFn: () =>
      findOrCreateChatRoom({
        kind: DIRECT_CHAT_ROOM,
        community_member_ids: [memberId],
      }),
    onSuccess: chatRoomUuid => {
      void navigate({
        ...chatRoomLinkOptions({
          uuid: chatRoomUuid,
        }),
        state: previousLocationState,
      });
      onMessageSent?.();
      closeParent?.();
    },
  });

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isViewOnlyMasquerading) {
      noop();
    } else {
      handleMessage();
    }
  };

  if (isCircleV4) {
    if (iconOnly) {
      return (
        <IconButton
          variant="ghost"
          icon="message-dots"
          loading={isLoading}
          disabled={disabled}
          onClick={handleClick}
          aria-label={t("message")}
          data-testid="message-button"
        />
      );
    }
    return (
      <CompassButton
        variant="outline"
        startIcon="message-dots"
        loading={isLoading}
        disabled={disabled}
        onClick={handleClick}
        aria-label={t("message")}
        data-testid="message-button"
        fullWidth={fullWidth}
        className={className}
      >
        {t("message")}
      </CompassButton>
    );
  }

  const isIconOnly = variant === "icon-only";
  const iconSize = iconSizes[size];

  return (
    <Button
      variant="secondary"
      onClick={handleClick}
      aria-label={t("message")}
      data-testid="message-button"
      large={size === "lg"}
      className={classNames(
        "flex min-w-30 items-center justify-center !gap-1 !border-primary !font-medium",
        sizeClasses[size],
        isIconOnly ? "!w-8 min-w-0 !px-0" : "w-full",
        className,
      )}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        <Icon type="loader" />
      ) : (
        <>
          <Icon
            type="16-bubble-annotation"
            size={iconSize}
            useWithFillCurrentColor
          />
          {!isIconOnly && (
            <Typography.LabelSm color="text-primary" weight="font-medium">
              {t("message")}
            </Typography.LabelSm>
          )}
        </>
      )}
    </Button>
  );
};
